import { useEffect, useRef, useState } from "react";

interface ProgressiveListResult<T> {
  /** The currently-mounted slice of items — grows to the full list over a few frames. */
  visible: T[];
  /** True while more items remain unmounted (e.g. to show a subtle "loading more" hint). */
  hasMore: boolean;
}

/**
 * Progressive mounting for large lists.
 *
 * The Projects/ProjectFair grids can hold hundreds of cards, each a Prism web component
 * with its own shadow DOM. Mounting all of them in a single React commit is the dominant
 * paint cost and blocks first paint. This mounts an initial batch immediately, then grows
 * by `step` on each animation frame until the whole list is rendered.
 *
 * Unlike scroll-gated virtualization, this does NOT hide data: every item ends up in the
 * DOM within a few frames, so the scrollbar is accurate, browser find (Ctrl+F) works, and
 * the user never has to scroll to reveal results. The win is spreading the web-component
 * upgrade cost across frames instead of one long blocking task.
 *
 * The count resets whenever the `items` reference changes (a new filter/search/sort
 * result). The reset happens *synchronously during render* — not in an effect — so a
 * changed result set never commits its full length in one giant pass before shrinking
 * back. Doing the reset post-commit caused a visible stall when toggling a filter while
 * the list was already fully expanded (e.g. adding several years): React would mount
 * every filtered card at once, then drop to the initial batch, then grow again.
 */
export function useProgressiveList<T>(
  items: T[],
  {
    initial = 24,
    step = 48,
    resetKey,
  }: { initial?: number; step?: number; resetKey?: unknown } = {}
): ProgressiveListResult<T> {
  const [count, setCount] = useState(initial);

  // Reset to the first batch synchronously when the underlying result set changes, or when
  // the caller-provided resetKey changes. Adjusting state during render (the supported
  // "derived from props" pattern) makes React discard this render's output and immediately
  // re-render with the smaller count, so the expensive full-length commit never lands.
  const prevItemsRef = useRef(items);
  const prevResetKeyRef = useRef(resetKey);
  if (prevItemsRef.current !== items || prevResetKeyRef.current !== resetKey) {
    prevItemsRef.current = items;
    prevResetKeyRef.current = resetKey;
    if (count !== initial) setCount(initial);
  }

  useEffect(() => {
    if (count >= items.length) return;

    const schedule =
      typeof requestAnimationFrame !== "undefined"
        ? requestAnimationFrame
        : (cb: FrameRequestCallback) => setTimeout(() => cb(0), 16) as unknown as number;
    const cancel =
      typeof cancelAnimationFrame !== "undefined"
        ? cancelAnimationFrame
        : (clearTimeout as unknown as (h: number) => void);

    const handle = schedule(() => {
      setCount((c) => Math.min(items.length, c + step));
    });
    return () => cancel(handle);
  }, [items, step, count]);

  return {
    visible: count >= items.length ? items : items.slice(0, count),
    hasMore: count < items.length,
  };
}
